<template>
  <AnalyticsCard
    title="Who books you"
    subtitle="Most frequent attendees, and when you last met"
    :loading="loading"
    :empty="!loading && data.length === 0"
    empty-title="No attendees in this period"
    empty-hint="The people who book you most often will be listed here."
    skeleton="list"
    :rows="5"
    :height="264"
  >
    <div class="divide-y divide-gray-50 dark:divide-gray-800/60">
      <a v-for="person in data" :key="person.id"
        :href="`/attendees?attendee=${encodeURIComponent(person.email || '')}`"
        data-turbo="false"
        class="flex items-center gap-3 px-5 py-2.5 hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors">
        <span class="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-semibold text-white"
          :style="{ backgroundColor: avatarColor(person.id) }">{{ person.initials }}</span>
        <span class="min-w-0 flex-1">
          <span class="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{{ person.name }}</span>
          <span v-if="person.last_booked_at" class="block text-[11px] text-gray-400 dark:text-gray-500 truncate">
            Last met {{ relative(person.last_booked_at) }}
          </span>
        </span>
        <span class="text-sm font-bold text-gray-900 dark:text-white tabular-nums flex-shrink-0">{{ person.bookings }}</span>
      </a>
    </div>
  </AnalyticsCard>
</template>

<script setup>
import AnalyticsCard from '../shared/AnalyticsCard.vue'
import { SERIES_COLORS } from '../shared/useAnalyticsChart.js'

defineProps({
  data:    { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
})

// Stable per person rather than positional, so somebody's avatar does not
// change colour when they move up the list.
function avatarColor(id) {
  const str = String(id)
  let hash = 0
  for (let i = 0; i < str.length; i += 1) hash = (hash * 31 + str.charCodeAt(i)) >>> 0
  return SERIES_COLORS[hash % SERIES_COLORS.length]
}

// "Last met" is only ever read for recency, so exact dates past a fortnight
// are less useful than the shape of the gap.
function relative(iso) {
  const then = new Date(iso)
  if (Number.isNaN(then.getTime())) return ''
  const days = Math.round((Date.now() - then.getTime()) / 86400000)

  if (days < 0) return `in ${Math.abs(days)} ${Math.abs(days) === 1 ? 'day' : 'days'}`
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 14) return `${days} days ago`
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(then)
}
</script>
